import { create } from 'zustand';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  confirmSignIn,
  signInWithRedirect,
  fetchAuthSession,
} from 'aws-amplify/auth';

import { handleAsync } from '@/utils';
import {
  useAppSelectionStore,
  useOrgStore,
  useSessionStore,
  useUserProfileStore,
  useRecordingStore,
} from '@/host/stores';

import { voLogger } from '@shared/utils/logger';
import { useChatPanelStore } from '@/apps/rocketchat/store/chatPanel.store';
import {
  getRcToken,
  getRcUserId,
  setRcCredentials,
  clearRcCredentials,
} from '@/apps/rocketchat/services/rcCredentials';
import { ApiService, UserType } from '@/shared/services/api.service';
import { getRcBaseUrl } from '@/apps/rocketchat/services/rcBaseUrl';
import { resetJitsiMeetingService } from '@/shared/singletons/jitsi-singleton';
import posthog, { isPostHogEnabled } from '@host/config/posthog';

const _log = voLogger('auth.store');

const getErrorMessage = (err: unknown): string => {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
};

interface AuthStore {
  user: any;
  loading: boolean;
  redirect?: boolean;
  error: string | null;
  signIn: (credentials: { username: string; password: string }) => Promise<any | null>;
  confirmSignIn: (params: { challengeResponse: string }) => Promise<any | null>;
  signOut: () => Promise<void>;
  checkSession: () => Promise<any | null>;
  setRedirect: (value: boolean) => void;
  providerSignIn: (provider: 'Google' | 'Microsoft' | 'Auth0') => Promise<void>;
  getToken: () => Promise<string | null>;
  refreshToken: () => Promise<string>;
  authenticateRocketChatUser: () => Promise<void>;
  authenticateRocketChatGuest: () => Promise<void>;
  linkIdentityWithProvider: () => Promise<any>;
  cognitoLocalSignOut: () => Promise<void>;
  getCognitoIdTokenForUser: () => Promise<string | null>;
  clearAuthStore: () => void;
  connectToSocket: (authToken: string, userId: string) => void;
  chatCount: number;
  rocketchatReady: boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  chatCount: 0,
  rocketchatReady: false,

  getCognitoIdTokenForUser: async () => {
    for (const key in localStorage) {
      if (key.includes('CognitoIdentityServiceProvider') && key.endsWith('.idToken')) {
        return localStorage.getItem(key);
      }
    }
    return null;
  },

  authenticateRocketChatUser: async () => {
    try {
      const existingToken = getRcToken();
      if (existingToken) {
        _log.debug('RocketChat user already authenticated');
        get().connectToSocket(existingToken, getRcUserId());
        return;
      }
      const testLocalStorageInterval = setInterval(async () => {
        const token = await get().getCognitoIdTokenForUser();
        if (!token) {
          _log.error('No Cognito ID token found for RocketChat authentication');
          return;
        }
        clearInterval(testLocalStorageInterval);

        const rocketchattoken = await ApiService.post<any>('rocketchatauth/session/init', {
          token,
        });

        setRcCredentials(rocketchattoken.token, rocketchattoken.userId);
        get().connectToSocket(rocketchattoken.token, rocketchattoken.userId);
      }, 100);
    } catch (error) {
      console.error('Error during authentication:', error);
    }
  },

  authenticateRocketChatGuest: async () => {
    try {
      const existingToken = getRcToken();
      if (existingToken) {
        _log.debug('RocketChat guest already authenticated');
        get().connectToSocket(existingToken, getRcUserId());
        return;
      }

      const POLL_INTERVAL = 100;

      const testLocalStorageInterval = setInterval(async () => {
        const isUserGuest = localStorage.getItem('isUserGuest');
        if (!isUserGuest) {
          _log.error('No guest user found for RocketChat authentication');
          return;
        }
        clearInterval(testLocalStorageInterval);

        const rocketchattoken = await ApiService.post<any>(
          'rocketchatauth/session/init-guest',
          undefined,
          { userType: UserType.GuestUser }
        );

        setRcCredentials(rocketchattoken.token, rocketchattoken.userId);
        get().connectToSocket(rocketchattoken.token, rocketchattoken.userId);

        if (rocketchattoken.roomId) {
          useChatPanelStore.getState().setGuestRestrictedRoomId(rocketchattoken.roomId);
        }
      }, POLL_INTERVAL);
    } catch (error) {
      console.error('Error during guest RocketChat authentication:', error);
    }
  },

  connectToSocket: (authToken: string, userId: string) => {
    try {
      let socket: WebSocket | null = null;
      let reconnectAttempts = 0;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let isIntentionallyClosed = false;
      let pingInterval: ReturnType<typeof setInterval> | null = null;
      let lastPongTime = Date.now();

      const MAX_RECONNECT_ATTEMPTS = 10;
      const INITIAL_RETRY_DELAY = 1000; // 1 second
      const MAX_RETRY_DELAY = 30000; // 30 seconds
      const PING_TIMEOUT = 60000; // 60 seconds without pong = dead connection
      const PING_INTERVAL = 30000; // Check every 30 seconds

      const httpBase = getRcBaseUrl();
      const wsBase = import.meta.env.VITE_WEB_SOCKET_SOURCE_URL!.replace(/\/+$/, '');
      const wsUrl = `${wsBase}/websocket`;

      let nextId = 0;
      const mkId = (p = 'id') => `${p}-${++nextId}`;

      // rid -> room name (so we can detect "general")
      const ridToName = new Map<string, string>();

      // Debounce unread refresh so rapid DDP events (e.g. own message echo) collapse into one sweep
      let unreadDebounceTimer: ReturnType<typeof setTimeout> | null = null;
      const scheduleFetchUnreadCounts = (delayMs = 1500) => {
        if (unreadDebounceTimer) clearTimeout(unreadDebounceTimer);
        unreadDebounceTimer = setTimeout(() => {
          unreadDebounceTimer = null;
          fetchUnreadCounts();
        }, delayMs);
      };

      const fetchUnreadCounts = async (): Promise<void> => {
        try {
          const headers: any = { 'X-Auth-Token': authToken, 'X-User-Id': userId };

          const [subsRes, roomsRes] = await Promise.all([
            fetch(`${httpBase}/api/v1/subscriptions.get`, { headers }),
            fetch(`${httpBase}/api/v1/rooms.get`, { headers }),
          ]);
          const [{ update = [] }, { update: roomsUpdate = [] }] = await Promise.all([
            subsRes.json(),
            roomsRes.json(),
          ]);

          for (const s of update) if (s?.rid && s?.name) ridToName.set(s.rid, s.name);

          // Rooms with no lastMessage have never had anything sent in them (e.g. auto-provisioned
          // lobby/office channels) — exclude their unread from the badge even if a subscription exists.
          const roomsWithMessages = new Set<string>();
          for (const r of roomsUpdate as any[]) if (r?.lastMessage) roomsWithMessages.add(r._id);

          // Force the chat panel's per-room unread counts to match this server-truth snapshot,
          // so a stale optimistic increment/correction race in the DDP stream can't leave the
          // room list out of sync. The nav badge is NOT set here: RocketChatBootstrap mirrors the
          // panel's totalUnread, the same number "Mark all read" uses. Summing subscriptions here
          // instead counted hidden (open: false) rooms the panel never lists — a badge with
          // nothing to mark read.
          const perRoomUnread: Record<string, number> = {};
          for (const s of update as any[]) {
            if (s?.rid) perRoomUnread[s.rid] = roomsWithMessages.has(s.rid) ? s?.unread || 0 : 0;
          }
          useChatPanelStore.getState().reconcileUnreadCounts(perRoomUnread);
        } catch {
          // Silently fail
        }
      };

      const cleanup = () => {
        try {
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
          }
          if (unreadDebounceTimer) {
            clearTimeout(unreadDebounceTimer);
            unreadDebounceTimer = null;
          }
          if (socket) {
            socket.onopen = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.onclose = null;
            if (
              socket.readyState === WebSocket.OPEN ||
              socket.readyState === WebSocket.CONNECTING
            ) {
              socket.close();
            }
            socket = null;
          }
        } catch (err) {
          console.log(err);
        }
      };

      const calculateRetryDelay = (attempt: number): number => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
        const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, attempt), MAX_RETRY_DELAY);
        // Add jitter to prevent thundering herd
        return delay + Math.random() * 1000;
      };

      const startPingMonitor = () => {
        try {
          if (pingInterval) {
            clearInterval(pingInterval);
          }

          lastPongTime = Date.now();

          pingInterval = setInterval(() => {
            try {
              const timeSinceLastPong = Date.now() - lastPongTime;

              // If we haven't received a pong in PING_TIMEOUT, connection is dead
              if (timeSinceLastPong > PING_TIMEOUT) {
                // Force reconnect
                if (socket && socket.readyState === WebSocket.OPEN) {
                  socket.close();
                }
                connect();
              }
            } catch (err) {
              console.log(err);
            }
          }, PING_INTERVAL);
        } catch (err) {
          console.log(err);
        }
      };

      const connect = () => {
        try {
          cleanup();

          socket = new WebSocket(wsUrl);

          socket.onopen = () => {
            try {
              reconnectAttempts = 0; // Reset on successful connection
              lastPongTime = Date.now();
              startPingMonitor();
              socket?.send(
                JSON.stringify({ msg: 'connect', version: '1', support: ['1', 'pre2', 'pre1'] })
              );
            } catch (err) {
              console.log(err);
            }
          };

          socket.onmessage = (ev) => {
            try {
              let msg: any;
              try {
                msg = JSON.parse(ev.data);
              } catch {
                return;
              }

              if (msg.msg === 'ping') {
                lastPongTime = Date.now(); // Update last activity time
                socket?.send(JSON.stringify({ msg: 'pong' }));
                return;
              }

              if (msg.msg === 'pong') {
                lastPongTime = Date.now(); // Update last activity time
                return;
              }

              if (msg.msg === 'connected') {
                lastPongTime = Date.now();
                socket?.send(
                  JSON.stringify({
                    msg: 'method',
                    method: 'login',
                    id: mkId('login'),
                    params: [{ resume: authToken }],
                  })
                );
                return;
              }

              if (msg.msg === 'result' && msg.id?.startsWith('login')) {
                if (msg.error) {
                  return;
                }

                // user-scoped streams
                const subs: [string, boolean][] = [
                  [`${userId}/notification`, false],
                  [`${userId}/subscriptions-changed`, false],
                  // You can also add:
                  // [`${userId}/message`, false],
                ];
                for (const p of subs) {
                  socket?.send(
                    JSON.stringify({
                      msg: 'sub',
                      id: mkId('sub'),
                      name: 'stream-notify-user',
                      params: p,
                    })
                  );
                }

                fetchUnreadCounts();
                return;
              }

              // Filter system messages from #general on notification events
              if (msg.msg === 'changed' && msg.collection === 'stream-notify-user') {
                const eventName = msg.fields?.eventName as string | undefined;

                if (eventName && eventName.endsWith('/notification')) {
                  // payload shapes vary slightly depending on RC version; handle the common cases:
                  const args = msg.fields?.args || [];
                  const n0 = args[0] || {};
                  const payload = n0.payload || n0; // sometimes the fields are at top-level
                  const rid = payload.rid || payload.room?._id || payload.data?.rid;
                  const message =
                    payload.message ||
                    payload.data?.message ||
                    payload.notification?.payload?.message;

                  const roomName = rid ? ridToName.get(rid) : undefined;

                  // If it's #general AND it's a system message (has m.t), drop it.
                  if (roomName === 'general' && message?.t) {
                    // swallow it (don't trigger badge refresh or any UI updates)
                    return;
                  }
                }

                // Debounce so rapid events (own-message echo, subscriptions flush) collapse into one sweep
                if (
                  eventName &&
                  (eventName.endsWith('/notification') ||
                    eventName.endsWith('/subscriptions-changed'))
                ) {
                  scheduleFetchUnreadCounts();
                }
                return;
              }

              if (msg.msg === 'ready') {
                // console.log('DDP ready', msg.subs);
              }

              // Update last activity time for any message
              lastPongTime = Date.now();
            } catch (err) {
              console.log(err);
            }
          };

          socket.onerror = () => {
            // Silently handle WebSocket errors
          };

          socket.onclose = () => {
            try {
              // Clean up ping monitor
              if (pingInterval) {
                clearInterval(pingInterval);
                pingInterval = null;
              }

              // Don't reconnect if intentionally closed
              if (isIntentionallyClosed) {
                return;
              }

              // Attempt to reconnect with exponential backoff
              if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                const delay = calculateRetryDelay(reconnectAttempts);
                reconnectAttempts++;

                reconnectTimer = setTimeout(() => {
                  connect();
                }, delay);
              }
            } catch (err) {
              console.log(err);
            }
          };
        } catch (err) {
          console.log(err);
          // If connection fails, try again
          try {
            if (!isIntentionallyClosed && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
              const delay = calculateRetryDelay(reconnectAttempts);
              reconnectAttempts++;

              reconnectTimer = setTimeout(() => {
                connect();
              }, delay);
            }
          } catch (err) {
            console.log(err);
          }
        }
      };

      // Handle browser online/offline events
      const handleOnline = () => {
        try {
          if (!isIntentionallyClosed) {
            // Reset reconnect attempts when coming back online
            reconnectAttempts = 0;
            connect();
          }
        } catch (err) {
          console.log(err);
        }
      };

      const handleOffline = () => {
        try {
          // Close existing connection when going offline
          cleanup();
        } catch (err) {
          console.log(err);
        }
      };

      // Handle visibility change (tab becomes active again)
      const handleVisibilityChange = () => {
        try {
          if (!document.hidden && !isIntentionallyClosed) {
            // Check if connection is alive when tab becomes visible
            if (!socket || socket.readyState !== WebSocket.OPEN) {
              reconnectAttempts = 0;
              connect();
            } else {
              // Verify connection is truly alive by checking last pong time
              const timeSinceLastPong = Date.now() - lastPongTime;
              if (timeSinceLastPong > PING_TIMEOUT) {
                connect();
              }
            }
          }
        } catch (err) {
          console.log(err);
        }
      };

      // Add event listeners
      try {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } catch (err) {
        console.log(err);
      }

      // Initial connection
      connect();

      // Lazy-load the DDP service so it stays out of the main bundle for non-Nexgen tenants.
      // Retries with exponential backoff so a transient network blip or mid-deploy 404
      // doesn't permanently freeze rocketchatReady=false until the next full page reload.
      let ddpImportCancelled = false;
      const initDDPWithRetry = async (attempt = 0): Promise<void> => {
        if (ddpImportCancelled) return;
        try {
          const { RocketChatDDPServiceInstance } =
            await import('@/apps/rocketchat/services/RocketChatDDPService');
          if (ddpImportCancelled) return;
          RocketChatDDPServiceInstance.init(authToken, userId);
          set({ rocketchatReady: true });
        } catch (err) {
          if (ddpImportCancelled) return;
          const MAX_DDP_RETRIES = 3;
          if (attempt >= MAX_DDP_RETRIES) {
            _log.error(
              '[connectToSocket] DDP chunk failed to load after retries; chat unavailable',
              err
            );
            return;
          }
          const delay = Math.min(1000 * Math.pow(2, attempt), 30_000);
          _log.warn(
            `[connectToSocket] DDP chunk load failed (attempt ${attempt + 1}/${MAX_DDP_RETRIES}), retrying in ${delay}ms`,
            err
          );
          await new Promise<void>((resolve) => setTimeout(resolve, delay));
          return initDDPWithRetry(attempt + 1);
        }
      };
      initDDPWithRetry();

      // Return cleanup function for disconnecting when needed
      return () => {
        isIntentionallyClosed = true;
        ddpImportCancelled = true;

        try {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        } catch (err) {
          console.log(err);
        }

        cleanup();
      };
    } catch (err) {
      console.log(err);
    }
  },

  signIn: async ({ username, password }) => {
    set({ loading: true, error: null });

    const [err, result] = await handleAsync(() => amplifySignIn({ username, password }));

    if (err || !result?.isSignedIn) {
      set({ loading: false, error: getErrorMessage(err) ?? 'Sign in failed' });
      return null;
    }

    const [userErr, user] = await handleAsync(() => getCurrentUser());
    await get().authenticateRocketChatUser();
    set({
      user: user ?? null,
      loading: false,
      error: userErr ? getErrorMessage(userErr) : null,
    });

    return result;
  },

  confirmSignIn: async ({ challengeResponse }) => {
    const { user } = get();
    if (!user) {
      set({ error: 'No user available to confirm sign in.' });
      return null;
    }

    const [err, result] = await handleAsync(() => confirmSignIn({ challengeResponse }));
    set({
      user: result ?? null,
      loading: false,
      error: err ? getErrorMessage(err) : null,
    });

    _log.debug('set user after login', get());
    return result;
  },

  signOut: async () => {
    try {
      const { user } = get();

      _log.debug('Signing out user:', user);

      if (user?.user_pid) {
        //GlobalEventService.emitTyped(new LoggedOutEvent({ user_pid: user.userId }));
      }

      // Disconnect RC only if it was initialised for this session; skipping the
      // import entirely means the DDP chunk is never downloaded for non-RC tenants.
      if (getRcToken()) {
        const ddp = await import('@/apps/rocketchat/services/RocketChatDDPService').catch(
          () => null
        );
        ddp?.RocketChatDDPServiceInstance.disconnect();
      }

      await amplifySignOut();
      if (isPostHogEnabled) {
        posthog.reset();
      }

      // rocketchatReady false unmounts RocketChatBootstrap, so it starts afresh (prefs, AI
      // Assistant, badge) for whoever signs in next without a page reload.
      set({ user: null, redirect: false, rocketchatReady: false, chatCount: 0 });

      await resetJitsiMeetingService();
      useOrgStore.getState().reset();
      useSessionStore.getState().clearSession();
      useUserProfileStore.getState().clear();
      useRecordingStore.getState().clearRecordingSession();
      useAppSelectionStore.getState().updateSlotSelection('full', null, null);
      useAppSelectionStore.getState().setOpenedApps([]);
      localStorage.removeItem('slotSelections');
      localStorage.removeItem('openedApps');
      localStorage.removeItem('orgPid');
      localStorage.removeItem('org-storage');
      clearRcCredentials();
      localStorage.removeItem('session-storage');
      localStorage.removeItem('workplace-store');
      localStorage.removeItem('appTheme');
      localStorage.removeItem('administration-store');
    } catch (err) {
      console.error('Sign out failed:', err);
      set({ error: getErrorMessage(err) });
    }
  },

  cognitoLocalSignOut: async () => {
    if (isPostHogEnabled) {
      posthog.reset();
    }

    set({ user: null, redirect: false });

    for (const key in localStorage) {
      if (key.includes('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key);
      }
    }
  },

  checkSession: async () => {
    const [err, result] = await handleAsync(() => getCurrentUser());

    set({
      user: result ?? null,
      error: err ? getErrorMessage(err) : null,
    });

    return result;
  },

  providerSignIn: async (provider) => {
    const providerConfig = provider === 'Google' ? provider : { custom: provider };
    await signInWithRedirect({ provider: providerConfig });
  },

  linkIdentityWithProvider: async () => {
    const user = get().user;
    if (!user) {
      set({ error: 'No user available to link identity.' });
      return;
    }
    const idToken = await get().getCognitoIdTokenForUser();
    return ApiService.post<any>('user-profile/identity/link', { idToken });
  },

  getToken: async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) return null;

      return session.tokens.accessToken.toString();
    } catch (err) {
      console.error('Error fetching token:', err);
      return null;
    }
  },

  refreshToken: async () => {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!session.tokens?.accessToken) {
      throw new Error('Session expired');
    }
    return session.tokens.accessToken.toString();
  },

  setRedirect: (value: boolean) => {
    set({ redirect: value });
  },

  clearAuthStore: () =>
    set({
      user: null,
      loading: false,
      error: null,
      redirect: false,
    }),
}));
